const INTERNAL_PREFIX = '[INTERNAL]';

/** Customer-choice mode needs at least two public lead sources so the list is a real choice. */
export const MIN_CUSTOMER_LEAD_SOURCE_CHOICES = 2;

/**
 * Format a lead source code for human-readable display.
 * Converts system codes like "FMM_-_Ocala" back to "FMM - Ocala".
 */
export function formatLeadSourceDisplay(code: string | null | undefined): string {
  if (!code) return '';
  return code.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Check if a lead source is marked as internal-only via its description/helpText field.
 */
export function isInternalLeadSource(helpText: string | null | undefined): boolean {
  return !!helpText?.startsWith(INTERNAL_PREFIX);
}

/**
 * Strip the `[INTERNAL]` prefix from a lead source helpText so the prefix
 * isn't shown to users. The prefix is purely a back-end marker and should
 * never leak into UI copy.
 */
export function stripInternalPrefix(helpText: string | null | undefined): string {
  if (!helpText) return '';
  return helpText.startsWith(INTERNAL_PREFIX)
    ? helpText.slice(INTERNAL_PREFIX.length).trim()
    : helpText;
}

const ASSIGNED_NOTE_PREFIX = 'This form is assigned to ';
const ASSIGNED_NOTE_SUFFIX = ' - Do NOT Use internally';

/**
 * Internal-note copy for a booking form whose lead source is predetermined
 * (every booking is auto-tagged). Warns the team not to reuse the form for
 * other channels, which would mix attribution.
 */
export function buildAssignedLeadSourceNote(leadSourceLabel: string): string {
  return `${ASSIGNED_NOTE_PREFIX}${leadSourceLabel}${ASSIGNED_NOTE_SUFFIX}`;
}

export function isAssignedLeadSourceNote(text: string | null | undefined): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  return trimmed.startsWith(ASSIGNED_NOTE_PREFIX) && trimmed.endsWith(ASSIGNED_NOTE_SUFFIX);
}

/**
 * Next internal-note value after a form is tagged with a predetermined lead
 * source. Empty notes and previously auto-generated notes are replaced. Pass
 * `replaceExisting: true` when the operator just picked the source so a
 * leftover note (e.g. from a duplicated form) does not hide the warning.
 */
export function nextInternalNoteForAssignedLeadSource(
  currentNote: string,
  leadSourceLabel: string,
  options?: { replaceExisting?: boolean },
): string {
  const next = buildAssignedLeadSourceNote(leadSourceLabel);
  if (options?.replaceExisting) return next;
  if (!currentNote.trim() || isAssignedLeadSourceNote(currentNote)) return next;
  return currentNote;
}

const DUPLICATE_NAME_SUFFIX = /\s*\(Copy\)\s*$/i;
const TRAILING_PARENS = /\s*\([^)]*\)\s*$/;

/**
 * Team-facing form name after a predetermined lead source is attached.
 * Matches the existing convention: `Sales Appointment (NEW ERA DIGITAL)`.
 * Strips a trailing `(Copy)` from duplication and replaces a previous
 * parenthetical source so suffixes do not stack.
 */
export function nextFormNameForAssignedLeadSource(
  currentName: string,
  leadSourceLabel: string,
  options?: { replaceExisting?: boolean },
): string {
  const trimmed = currentName.trim();
  if (!trimmed) return trimmed;

  const suffix = `(${leadSourceLabel})`;
  const withoutCopy = trimmed.replace(DUPLICATE_NAME_SUFFIX, '').trim();
  if (withoutCopy.endsWith(suffix)) return withoutCopy;
  if (!options?.replaceExisting) return trimmed;

  const base = withoutCopy.replace(TRAILING_PARENS, '').trim();
  if (!base) return suffix;
  return `${base} ${suffix}`;
}
